import { useEffect, useState } from "react";
import AdminListToolkit from "../../components/listing/AdminListToolkit";
import DataTable from "../../components/DataTable";
import GeneralTradeDocumentForm from "../../components/generalTrade/GeneralTradeDocumentForm";
import { gtApi } from "../../api/generalTradeApi";
import { useFormat } from "../../../context/FormatContext";
import { useBusy } from "../../../context/BusyContext";
import { useMessageModal } from "../../contexts/MessageModalContext";

type SalesOrderScreenMode = "list" | "add" | "edit" | "view";

interface SalesOrderRow {
  id: number;
  sales_order_number: string;
  order_date: string;
  company_name: string;
  grand_total: number | string;
  created_at: string;
  updated_at: string;
  status: string;

  is_locked?: number | boolean;
  is_editable?: number | boolean;

  active_delivery_order_id?: number | null;
  active_delivery_order_number?: string | null;
  active_delivery_order_count?: number;
  total_delivery_order_count?: number;
}

export default function SalesOrders() {
  const { formatDateToDMY, formatDateOnly, formatCurrency } = useFormat();
  const { showModal } = useMessageModal();
  const { runBusy } = useBusy();

  const [rows, setRows] = useState<SalesOrderRow[]>([]);
  const [selected, setSelected] = useState<SalesOrderRow | null>(null);

  const [screenMode, setScreenMode] = useState<SalesOrderScreenMode>("list");

  const [activeSalesOrderId, setActiveSalesOrderId] = useState<number | null>(
    null,
  );

  const load = async (): Promise<void> => {
    try {
      const salesOrders = await runBusy(() => gtApi.salesOrders());

      const safeRows = Array.isArray(salesOrders) ? salesOrders : [];

      setRows(safeRows);

      setSelected((previous) => {
        if (!previous) return null;

        return (
          safeRows.find(
            (row: SalesOrderRow) => Number(row.id) === Number(previous.id),
          ) || null
        );
      });
    } catch (error) {
      console.error("Failed to load Sales Orders:", error);
      setRows([]);
      setSelected(null);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const openAdd = () => {
    setActiveSalesOrderId(null);
    setScreenMode("add");
  };

  const openView = (row: SalesOrderRow) => {
    setActiveSalesOrderId(Number(row.id));
    setScreenMode("view");
  };

  const openEdit = (row: SalesOrderRow) => {
    if (row.status === "cancelled") {
      showModal({
        type: "error",
        title: "Sales Order Cancelled",
        message:
          "A cancelled Sales Order cannot be edited. Reactivate it first.",
        confirmText: "OK",
      });

      return;
    }

    const isLocked =
      Number(row.is_locked || 0) === 1 || Number(row.is_editable ?? 1) === 0;

    if (isLocked) {
      showModal({
        type: "error",
        title: "Sales Order View Only",
        message: row.active_delivery_order_number
          ? `This Sales Order is linked to active Delivery Order ${row.active_delivery_order_number} and cannot be edited.`
          : "This Sales Order is linked to an active Delivery Order and cannot be edited.",
        confirmText: "OK",
      });

      return;
    }

    setActiveSalesOrderId(Number(row.id));

    setScreenMode("edit");
  };

  const returnToList = () => {
    setScreenMode("list");
    setActiveSalesOrderId(null);
  };

  const handleSaved = () => {
    returnToList();
    setSelected(null);
    void load();
  };

  const changeSelectedSalesOrderStatus = async (): Promise<void> => {
    if (!selected) {
      showModal({
        type: "error",
        title: "Sales Order Required",
        message: "Please select a Sales Order first.",
        confirmText: "OK",
      });

      return;
    }

    const isCancelled =
      String(selected.status || "").toLowerCase() === "cancelled";

    /*
     * =========================
     * REACTIVATE
     * =========================
     */
    if (isCancelled) {
      showModal({
        type: "warning",
        title: "Reactivate Sales Order?",
        message: `${selected.sales_order_number} will be restored to Submitted status.`,

        confirmText: "Yes, Activate",

        onConfirm: async () => {
          try {
            const response = await runBusy(() =>
              gtApi.reactivateSalesOrder(Number(selected.id)),
            );

            showModal({
              type: "success",
              title: "Sales Order Reactivated",
              message:
                response?.message ||
                `${selected.sales_order_number} has been reactivated successfully.`,
              confirmText: "OK",
            });

            setSelected(null);

            await load();
          } catch (error: any) {
            showModal({
              type: "error",
              title: "Unable to Reactivate Sales Order",
              message:
                error?.response?.data?.message ||
                "Unable to reactivate the Sales Order.",
              confirmText: "OK",
            });
          }
        },
      });

      return;
    }

    /*
     * =========================
     * CANCEL
     * =========================
     */
    const isLocked =
      Number(selected.is_locked || 0) === 1 ||
      Number(selected.is_editable ?? 1) === 0;

    if (isLocked) {
      showModal({
        type: "error",
        title: "Sales Order Cannot Be Cancelled",
        message: selected.active_delivery_order_number
          ? `Active Delivery Order ${selected.active_delivery_order_number} exists. Cancel, Hold or Delete that Delivery Order first.`
          : "An active Delivery Order exists for this Sales Order.",
        confirmText: "OK",
      });

      return;
    }

    showModal({
      type: "warning",
      title: "Cancel Sales Order?",
      message: `This will cancel ${selected.sales_order_number}. Continue?`,

      confirmText: "Yes, Cancel",

      onConfirm: async () => {
        try {
          const response = await runBusy(() =>
            gtApi.deleteSalesOrder(Number(selected.id)),
          );

          showModal({
            type: "success",
            title: "Sales Order Cancelled",
            message:
              response?.message ||
              `${selected.sales_order_number} has been cancelled successfully.`,
            confirmText: "OK",
          });

          setSelected(null);

          await load();
        } catch (error: any) {
          showModal({
            type: "error",
            title: "Unable to Cancel Sales Order",
            message:
              error?.response?.data?.message ||
              "Unable to cancel the Sales Order.",
            confirmText: "OK",
          });
        }
      },
    });
  };

  const columns = [
    {
      key: "sales_order_number",
      label: "SO No.",
    },
    {
      key: "order_date",
      label: "Order Date",
      render: (row: SalesOrderRow) =>
        row.order_date ? formatDateOnly(row.order_date) : "-",
    },
    {
      key: "company_name",
      label: "Customer",
    },
    {
      key: "grand_total",
      label: "Amount",
      render: (row: SalesOrderRow) =>
        formatCurrency(Number(row.grand_total || 0)),
    },
    {
      key: "active_delivery_order_number",
      label: "Active DO",
      render: (row: SalesOrderRow) => row.active_delivery_order_number || "-",
    },
    {
      key: "edit_status",
      label: "Edit Status",
      render: (row: SalesOrderRow) => {
        const locked =
          Number(row.is_locked || 0) === 1 ||
          Number(row.is_editable ?? 1) === 0;

        return (
          <span className={locked ? "badge-danger" : "badge-success"}>
            {locked ? "View Only" : "Editable"}
          </span>
        );
      },
    },
    {
      key: "created_at",
      label: "Created At",
      render: (row: SalesOrderRow) =>
        row.created_at ? formatDateToDMY(row.created_at) : "-",
    },
    {
      key: "updated_at",
      label: "Updated At",
      render: (row: SalesOrderRow) =>
        row.updated_at ? formatDateToDMY(row.updated_at) : "-",
    },
    {
      key: "status",
      label: "Status",
      render: (row: SalesOrderRow) => (
        <span
          className={
            row.status === "submitted"
              ? "badge-success"
              : row.status === "cancelled"
                ? "badge-danger"
                : "badge-warning"
          }
        >
          {String(row.status || "")
            .charAt(0)
            .toUpperCase() + String(row.status || "").slice(1)}
        </span>
      ),
    },
  ];

  if (screenMode !== "list") {
    return (
      <GeneralTradeDocumentForm
        mode={screenMode}
        salesOrderId={activeSalesOrderId}
        onCancel={returnToList}
        onSaved={handleSaved}
      />
    );
  }

  return (
    <AdminListToolkit
      title="SALES ORDERS"
      columns={columns}
      data={rows}
      selectedRow={selected}
      onAdd={openAdd}
      onView={(row) => openView(row)}
      onEdit={(row) => openEdit(row)}
      onSoftDelete={() => {
        void changeSelectedSalesOrderStatus();
      }}
      softDeleteLabel={selected?.status === "cancelled" ? "Active" : "Delete"}
      softDeleteIsActivate={selected?.status === "cancelled"}
      editDisabled={
        selected?.status === "cancelled" ||
        Number(selected?.is_locked || 0) === 1 ||
        Number(selected?.is_editable ?? 1) === 0
      }
      enableExport
      exportFileName="sales-orders"
      extraButtons={[
        {
          label: "PDF",
          onClick: () => {
            if (!selected) {
              return;
            }

            window.open(gtApi.pdfUrl("sales-orders", selected.id), "_blank");
          },
        },

        {
          label: "Share",
          onClick: async () => {
            if (!selected) {
              return;
            }

            const url = gtApi.pdfUrl("sales-orders", selected.id);

            if (navigator.share) {
              await navigator.share({
                title: selected.sales_order_number,
                url,
              });
            } else {
              await navigator.clipboard.writeText(url);

              showModal({
                type: "success",
                title: "Link Copied",
                message: "Sales Order PDF link copied to clipboard.",
                confirmText: "OK",
              });
            }
          },
        },
      ]}
    >
      {(filteredRows) => (
        <DataTable
          columns={columns}
          data={filteredRows}
          selectedRowId={selected?.id}
          onRowSelect={setSelected}
        />
      )}
    </AdminListToolkit>
  );
}
